import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { StrongCustomerAuthenticationService } from '@paysafe/paysafe-wallet-saas-web/authentication';
import {
  ScaAuthenticationEventAttemptEmbeddedHybridResponse,
  ScaAuthenticationEventAttemptStatus, ScaAuthenticationEventChallengeEmbeddedHybridRequest,
  ScaAuthenticationEventChallengeEmbeddedHybridResponse
} from '@paysafe/paysafe-wallet-saas-web/common';
import { PsButtonModule } from '@paysafe-ui/components/button';
import { PsCardModule } from '@paysafe-ui/components/card';
import { PsFormFieldModule } from '@paysafe-ui/components/form-field';
import { PsIconComponent } from '@paysafe-ui/components/icon';
import { PsInputModule } from '@paysafe-ui/components/input';
import { PsProgressSpinnerComponent } from '@paysafe-ui/components/progress-spinner';
import { from, interval, Subscription } from 'rxjs';
import { finalize, map, takeWhile } from 'rxjs/operators';

import { ScaConstants } from '../../../constants/sca.const';
import { CheckoutErrorHandlerService } from '../../../services/error-handler/checkout-error-handler.service';

@Component({
  selector: 'app-sca-code-verification',
  templateUrl: './sca-code-verification.component.html',
  styleUrls: ['./sca-code-verification.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, PsButtonModule, PsCardModule, PsFormFieldModule, PsInputModule, PsIconComponent,
    PsProgressSpinnerComponent]
})
export class ScaCodeVerificationComponent implements OnInit, OnDestroy {
  @Input() activeChallenge: ScaAuthenticationEventChallengeEmbeddedHybridResponse;

  @Output() close = new EventEmitter<void>();
  @Output() chooseDifferentOption = new EventEmitter<void>();
  @Output() verified = new EventEmitter<void>();
  @Output() error = new EventEmitter<void>();

  verificationCode = '';
  otpCodeFieldError = '';
  remainingCodeSubmitAttempts: number = null;
  resendSecondsRemaining = 0;
  isSubmittingCode = false;
  isSendingCode = false;

  private timerSubscription?: Subscription;

  constructor(private scaService: StrongCustomerAuthenticationService,
              private checkoutErrorHandler: CheckoutErrorHandlerService) {
    // Dependency injection
  }

  ngOnInit(): void {
    this.startResendTimer();
  }

  ngOnDestroy(): void {
    this.timerSubscription?.unsubscribe();
  }

  onVerificationCodeSubmit(event: Event): void {
    event.preventDefault();
    this.isSubmittingCode = true;

    const request = {
      walletOperationId: this.activeChallenge.walletOperationId,
      verification: this.activeChallenge.verification,
      value: this.verificationCode
    };
    from(this.scaService.submitScaAttempt(this.activeChallenge.eventId, request))
      .pipe(finalize((): void => {
        this.isSubmittingCode = false;
      }))
      .subscribe({
        next: (response: ScaAuthenticationEventAttemptEmbeddedHybridResponse) => {
          this.remainingCodeSubmitAttempts = response.allowableAttempts - response.currentAttempts;
          if (response.status === ScaAuthenticationEventAttemptStatus.VERIFIED) {
            this.verified.emit();
          } else {
            this.otpCodeFieldError = ScaConstants.INVALID_CODE_MESSAGE;
          }
        },
        error: (error): void => {
          this.checkoutErrorHandler.handleScaError(error, this.activeChallenge.verification.channel);
          this.error.emit();
        }
      });
  }

  resendCode(event: Event): void {
    event.preventDefault();
    this.isSendingCode = true;
    this.resetFields();

    const request: ScaAuthenticationEventChallengeEmbeddedHybridRequest = {
      walletOperationId: this.activeChallenge.walletOperationId,
      verification: {
        method: this.activeChallenge.verification?.method,
        channel: this.activeChallenge.verification?.channel
      }
    };

    from(this.scaService.sendScaChallenge(this.activeChallenge.eventId, request))
      .pipe(finalize((): void => {
        this.isSendingCode = false;
      }))
      .subscribe({
        next: (response: ScaAuthenticationEventChallengeEmbeddedHybridResponse) => {
          this.activeChallenge = response;
          this.startResendTimer();
        },
        error: (error): void => {
          this.checkoutErrorHandler.handleScaError(error, this.activeChallenge.verification.channel);
          this.error.emit();
        }
      });
  }

  resetFields(): void {
    this.timerSubscription?.unsubscribe();
    this.remainingCodeSubmitAttempts = null;
    this.verificationCode = '';
    this.otpCodeFieldError = '';
  }

  isCodeValid(): boolean {
    return this.verificationCode.length === 6 && !this.isSubmittingCode;
  }

  onCodeInput(event: Event): void {
    this.verificationCode = (event.target as HTMLInputElement).value;
    if (this.verificationCode.length === 0) {
      this.otpCodeFieldError = ScaConstants.CODE_IS_REQUIRED;
    } else if (this.verificationCode.length < 6) {
      this.otpCodeFieldError = ScaConstants.CODE_MUST_BE_SIX_DIGITS;
    } else {
      this.otpCodeFieldError = '';
    }
  }

  startResendTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.resendSecondsRemaining = ScaConstants.RESEND_COOLDOWN_SECONDS;
    let remaining = ScaConstants.RESEND_COOLDOWN_SECONDS;
    this.timerSubscription = interval(1000).pipe(
      map(() => {
        --remaining;
        return remaining;
      }),
      takeWhile(sec => sec >= 0, true)
    ).subscribe(sec => {
      this.resendSecondsRemaining = Math.max(0, sec);
    });
  }

  formatResendTimer(): string {
    const minutes = Math.floor(this.resendSecondsRemaining / 60);
    const seconds = this.resendSecondsRemaining % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  get isTimerCounting(): boolean {
    return this.resendSecondsRemaining > 0;
  }

  getChannelIcon(): string {
    return ScaConstants.OTP_CHANNEL_CHECKED_ICONS[this.activeChallenge?.verification?.channel];
  }

  onClose(): void {
    this.close.emit();
  }

  onChooseDifferentOption(): void {
    this.chooseDifferentOption.emit();
  }
}
